import { Box, useIsDarkMode, useTheme } from '@chronosphereio/chrono-ui';
import { acceptCompletion, closeCompletion, completionStatus } from '@codemirror/autocomplete';
import { insertTab } from '@codemirror/commands';
import { Prec } from '@codemirror/state';
import { EditorView, type KeyBinding, keymap } from '@codemirror/view';
import type { ReactCodeMirrorProps, ReactCodeMirrorRef } from '@uiw/react-codemirror';
import { forwardRef, useMemo } from 'react';
import { CodeMirror } from './codemirror-shim';
import { darkTheme, lightTheme } from './theme';

export type TabBehavior = 'indent' | 'focus';

// Inserts a tab character when the tab key is pressed
export const createTabKeyBinding = (tabBehavior: TabBehavior): KeyBinding => ({
  key: 'Tab',
  run: (view: EditorView) => {
    // If the completion is active, accept the completion
    if (completionStatus(view.state) === 'active') {
      acceptCompletion(view);
      return true;
    }
    if (tabBehavior === 'indent') {
      // Otherwise, insert a tab character
      insertTab(view);
      return true;
    }
    // In focus mode, still allow block indentation when users have an explicit selection.
    const hasSelectedText = view.state.selection.ranges.some((range) => range.from !== range.to);
    if (hasSelectedText) {
      insertTab(view);
      return true;
    }
    // Let browser tab to the next/previous focusable element.
    return false;
  },
});

// Eg stop Drawer from closing on Esc key press
const stopPropagationOnEsc = {
  key: 'Escape',
  preventDefault: true,
  stopPropagation: true,
  run: () => {
    return false;
  },
};

const codeEditorKeymap = (onRun?: () => void, tabBehavior: TabBehavior = 'indent') => {
  const run = (view: EditorView) => {
    closeCompletion(view);
    if (onRun) {
      onRun();
    }
    return true;
  };
  const keymaps = [
    {
      key: 'Cmd-Enter',
      run,
    },
    {
      key: 'Ctrl-Enter',
      run,
    },
    stopPropagationOnEsc,
    createTabKeyBinding(tabBehavior),
  ];
  return keymap.of(keymaps);
};

export interface CodeEditorProps extends Omit<ReactCodeMirrorProps, 'onBlur'> {
  ref?: React.Ref<ReactCodeMirrorRef>;
  onRun?: () => void;
  onBlur?: () => void;
  /**
   * The behavior of the tab key in the code editor.
   * - 'indent': Inserts a tab character.
   * - 'focus': Tabs to the next/previous focusable element except when the completion is active or there is selected text.
   * @default 'indent'
   */
  tabBehavior?: TabBehavior;
  /**
   * Preview has a gray background and is read-only.
   */
  variant?: 'preview';
  /**
   * Disable the border around the code editor
   */
  disableBorder?: boolean;
  /**
   * Content to display on the right side of the code editor
   */
  endAdornment?: React.ReactNode;
}

/**
 * Code editor powered by CodeMirror v6. For more information, see the
 * [react-codemirror docs](https://github.com/uiwjs/react-codemirror)
 */
export const CodeEditor = forwardRef<ReactCodeMirrorRef, CodeEditorProps>(function CodeEditor(
  {
    onRun,
    tabBehavior = 'indent',
    theme,
    variant,
    disableBorder = false,
    basicSetup,
    endAdornment,
    readOnly,
    ...rest
  }: CodeEditorProps,
  ref
) {
  const isDarkMode = useIsDarkMode();
  const defaultTheme = isDarkMode ? darkTheme : lightTheme;
  const { theme: t } = useTheme();

  if (variant === 'preview') {
    readOnly = true;
  }

  const basicSetupMerged = useMemo(() => {
    if (typeof basicSetup === 'boolean') {
      return basicSetup ? basicSetup : undefined;
    }
    const basicOptions: ReactCodeMirrorProps['basicSetup'] = {
      syntaxHighlighting: true,
      autocompletion: false,
      highlightActiveLine: false,
      highlightActiveLineGutter: false,
      highlightSelectionMatches: readOnly ? false : true,
      foldGutter: false,
      lineNumbers: false,
      searchKeymap: false,
      ...basicSetup,
    };
    return basicOptions;
  }, [basicSetup, readOnly]);

  const extensions = useMemo(() => {
    const editorTheme = EditorView.theme({
      '&.cm-editor': {
        backgroundColor: variant === 'preview' ? t.palette.background.canvas : 'inherit',
      },
      '&.cm-editor.cm-focused': {
        outline: 'none',
      },
      '.cm-diagnosticAction': {
        marginLeft: 0,
        backgroundColor: 'rgb(208, 208, 208)',
        color: 'black',
        marginBottom: '4px',
      },
      '&.cm-content': {
        cursor: readOnly ? 'default' : null,
      },
    });

    // combine extensions
    let extensions = rest.extensions ?? [];
    if (readOnly) {
      extensions = [...extensions, EditorView.editable.of(false)];
    }

    // This will add a `data-value` property to the contenteditable (textbox) element with the current text value
    // in the input (can be used in Playwright tests to assert / check the current value)
    const addValueToDom = EditorView.contentAttributes.of((v) => ({ 'data-value': v.state.doc.toString() }));

    extensions = [
      ...extensions,
      EditorView.lineWrapping,
      Prec.high(codeEditorKeymap(onRun, tabBehavior)),
      editorTheme,
      addValueToDom,
    ];
    return extensions;
  }, [variant, t.palette.background.canvas, readOnly, rest.extensions, onRun, tabBehavior]);

  return (
    <Box
      sx={{
        backgroundColor: variant === 'preview' ? t.palette.background.canvas : undefined,
        border: disableBorder || variant === 'preview' ? undefined : `solid 1px ${t.palette.divider}`,
        borderRadius: disableBorder ? undefined : 1,
        padding: disableBorder ? undefined : 1,
      }}
      display="flex"
    >
      <Box flex={1} alignContent="center">
        <CodeMirror
          indentWithTab={false}
          placeholder="Query"
          basicSetup={basicSetupMerged}
          ref={ref}
          theme={theme ?? defaultTheme}
          {...rest}
          extensions={extensions}
        />
      </Box>
      {endAdornment}
    </Box>
  );
});
